import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { groupName } from '../../utils/userGroupDisplay';
import {
  FiArrowLeft, FiPlus, FiTrash2, FiCopy, FiChevronUp, FiChevronDown,
  FiChevronRight, FiCheck, FiX, FiRefreshCw, FiGitBranch, FiInfo, FiTag,
  FiLayers, FiUser, FiUsers, FiBriefcase, FiClock, FiMail,
  FiBell, FiCornerUpRight, FiCheckCircle, FiXCircle, FiFileText, FiEdit3,
  FiShield, FiSliders, FiShare2, FiLock, FiUploadCloud, FiMessageSquare,
  FiCheckSquare, FiAlertTriangle,
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import {
  workflowsAPI, positionsAPI, titlesAPI, usersAPI, groupsAPI, companiesAPI,
} from '../../services/api';
import SearchableSelect from '../../components/SearchableSelect';
import ConfirmDialog from '../../components/ConfirmDialog';
import CompanyScopeSelect, { canScopeAllCompanies, defaultScopeForUser } from '../../components/CompanyScopeSelect/CompanyScopeSelect';
import { formatDateTimeFull } from '../../utils/dateFormat';
import MultiLangInput from '../../components/MultiLangInput';
import {
  emptyWorkflow, emptyStep, workflowToForm, toDHM, fromDHM, formatDuration,
  hasEscalationTarget, EMAIL_PLACEHOLDERS, MIN_TIMEOUT_MINUTES, MAX_TIMEOUT_MINUTES,
  REVIEW_ACTION_KEYS, reviewActionsAll, STEP_ABILITY_KEYS, ORG_RULE_KEYS, DOC_ACTORS_ORG_RULE, localizeSeeded,
  genStepKey,
} from './flowUtils';
import './FlowBuilder.scss';

// The things a step can be. The order is the one shown on the card:
// review → control → approval → information, from most to least involved.
// Control (Kontrol): a continue-or-stop gate — Uygun Görüldü advances the
// flow, Uygun Görülmedi ends the run with a mandatory reason.
const STEP_TYPES = [
  { value: 'review', icon: FiEdit3 },
  { value: 'control', icon: FiCheckSquare },
  { value: 'approval', icon: FiCheck },
  { value: 'information', icon: FiBell },
];

let stepKeyCounter = 0;
const nextStepKey = () => `sk-${++stepKeyCounter}`;
const withKeys = (steps) => steps.map(s => ({ ...s, _key: s._key || nextStepKey() }));

const clampTimeout = (mins) =>
  Math.min(Math.max(Math.round(Number(mins) || 0), MIN_TIMEOUT_MINUTES), MAX_TIMEOUT_MINUTES);

// ─────────────────────────────────────────────────────────────────────────────
// Assignee picker — segmented type control + searchable select
// ─────────────────────────────────────────────────────────────────────────────
// i18n key suffix per org rule (flows.orgRules.<key> / <key>Desc)
const ORG_RULE_I18N = {
  initiator: 'initiator',
  initiator_manager: 'initiatorManager',
  initiator_dept_head: 'initiatorDeptHead',
  responsible_manager: 'responsibleManager',
  responsible_dept_head: 'responsibleDeptHead',
  document_actors: 'documentActors',
};

function AssigneePicker({ value, onChange, options, compact = false, allowOrganization = false, orgRules = ORG_RULE_KEYS }) {
  const { t } = useTranslation();
  const type = value.assigneeType || 'user';

  const TYPES = [
    { value: 'position', label: t('flows.position'), icon: FiBriefcase },
    { value: 'title', label: t('flows.titleLabel'), icon: FiTag },
    { value: 'user', label: t('flows.specificUser'), icon: FiUser },
    { value: 'group', label: t('flows.userGroup'), icon: FiUsers },
    ...(allowOrganization
      ? [{ value: 'organization', label: t('flows.organization'), icon: FiShare2 }]
      : []),
  ];

  const fieldByType = {
    position: 'assigneePosition',
    title: 'assigneeTitle',
    user: 'assigneeUser',
    group: 'assigneeGroup',
  };
  const placeholderByType = {
    position: t('flows.selectPosition'),
    title: t('flows.selectTitle'),
    user: t('flows.selectUser'),
    group: t('flows.selectGroup'),
  };

  return (
    <div className={`fb__assignee ${compact ? 'fb__assignee--compact' : ''}`}>
      <div className="fb__segctrl">
        {TYPES.map(opt => {
          const Icon = opt.icon;
          return (
            <button
              key={opt.value}
              type="button"
              className={`fb__segctrl-btn ${type === opt.value ? 'is-active' : ''}`}
              onClick={() => onChange({ assigneeType: opt.value })}
            >
              <Icon /> <span>{opt.label}</span>
            </button>
          );
        })}
      </div>
      {type === 'organization' ? (
        <>
          <div className="fb__org-rules">
            {orgRules.map(rule => {
              const active = value.assigneeOrgRule === rule;
              return (
                <button
                  key={rule}
                  type="button"
                  className={`fb__org-rule ${active ? 'is-active' : ''}`}
                  onClick={() => onChange({ assigneeOrgRule: rule })}
                >
                  <span className="fb__org-rule-radio" aria-hidden="true" />
                  <span className="fb__org-rule-texts">
                    <span className="fb__org-rule-label">{t(`flows.orgRules.${ORG_RULE_I18N[rule]}`)}</span>
                    <span className="fb__org-rule-desc">{t(`flows.orgRules.${ORG_RULE_I18N[rule]}Desc`)}</span>
                  </span>
                </button>
              );
            })}
          </div>
          <p className="fb__hint">{t('flows.orgRules.dynamicHint')}</p>
        </>
      ) : (
        <SearchableSelect
          value={value[fieldByType[type]] || ''}
          onChange={(v) => onChange({ [fieldByType[type]]: v || null })}
          options={options[type] || []}
          placeholder={placeholderByType[type]}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Timeout composer — days / hours / minutes steppers + live summary
// ─────────────────────────────────────────────────────────────────────────────
function TimeoutComposer({ minutes, onChange }) {
  const { t } = useTranslation();
  const dhm = toDHM(minutes);

  const update = (field, raw) => {
    const v = Math.max(0, parseInt(raw, 10) || 0);
    const next = { ...dhm, [field]: field === 'days' ? Math.min(v, 180) : field === 'hours' ? Math.min(v, 23) : Math.min(v, 59) };
    onChange(clampTimeout(fromDHM(next)));
  };

  const UNITS = [
    { field: 'days', label: t('flows.builder.timeoutDays'), max: 180 },
    { field: 'hours', label: t('flows.builder.timeoutHours'), max: 23 },
    { field: 'minutes', label: t('flows.builder.timeoutMinutesLabel'), max: 59 },
  ];

  return (
    <div className="fb__timeout">
      <div className="fb__timeout-units">
        {UNITS.map(u => (
          <div key={u.field} className="fb__timeout-unit">
            <div className="fb__stepper">
              <button type="button" className="fb__stepper-btn" onClick={() => update(u.field, dhm[u.field] - 1)} tabIndex={-1}>−</button>
              <input
                type="number"
                min={0}
                max={u.max}
                value={dhm[u.field]}
                onChange={e => update(u.field, e.target.value)}
              />
              <button type="button" className="fb__stepper-btn" onClick={() => update(u.field, dhm[u.field] + 1)} tabIndex={-1}>+</button>
            </div>
            <span className="fb__timeout-unit-label">{u.label}</span>
          </div>
        ))}
      </div>
      <div className="fb__timeout-summary">
        <FiClock />
        {t('flows.builder.timeoutSummary', { duration: formatDuration(clampTimeout(minutes), t) })}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Email template editor — tabs (approval / timeout / escalation)
// ─────────────────────────────────────────────────────────────────────────────
function EmailTemplateEditor({ emails, onChange, escalationEnabled, informationOnly }) {
  const { t } = useTranslation();
  const [tab, setTab] = useState('approval');
  const bodyRef = useRef(null);

  // An information step waits for nobody, so it can never time out or escalate
  // — it has exactly one message, the notification itself.
  const TABS = informationOnly ? [
    { key: 'approval', label: t('flows.builder.emailTabInformation'), icon: FiBell },
  ] : [
    { key: 'approval', label: t('flows.builder.emailTabApproval'), icon: FiMail },
    { key: 'timeout', label: t('flows.builder.emailTabTimeout'), icon: FiClock },
    { key: 'escalation', label: t('flows.builder.emailTabEscalation'), icon: FiCornerUpRight, disabled: !escalationEnabled },
  ];

  // Switching a step to Information while the timeout tab is open would
  // otherwise leave the editor pointed at a tab that no longer exists.
  const activeTab = informationOnly ? 'approval' : tab;
  const current = emails[activeTab] || { enabled: true, subject: '', body: '' };
  const patch = (p) => onChange(activeTab, p);

  const insertPlaceholder = (name) => {
    const token = `{{${name}}}`;
    const el = bodyRef.current;
    if (el && document.activeElement === el) {
      const start = el.selectionStart ?? current.body.length;
      const end = el.selectionEnd ?? start;
      patch({ body: current.body.slice(0, start) + token + current.body.slice(end) });
    } else {
      patch({ body: (current.body || '') + token });
    }
  };

  const usePreset = () => {
    patch({
      subject: t(`flows.builder.presets.${informationOnly ? 'information' : activeTab}.subject`),
      body: t(`flows.builder.presets.${informationOnly ? 'information' : activeTab}.body`),
    });
  };

  return (
    <div className="fb__emails">
      <div className="fb__email-tabs">
        {TABS.map(tb => {
          const Icon = tb.icon;
          return (
            <button
              key={tb.key}
              type="button"
              disabled={tb.disabled}
              className={`fb__email-tab ${activeTab === tb.key ? 'is-active' : ''}`}
              onClick={() => setTab(tb.key)}
            >
              <Icon /> {tb.label}
            </button>
          );
        })}
      </div>

      <div className="fb__email-panel">
        <div className="fb__email-toolbar">
          <label className="fb__switch">
            <input
              type="checkbox"
              checked={current.enabled !== false}
              onChange={e => patch({ enabled: e.target.checked })}
            />
            <span className="fb__switch-track"><span className="fb__switch-thumb" /></span>
            <span className="fb__switch-label">{t('flows.builder.emailSendToggle')}</span>
          </label>
          <button type="button" className="fb__btn fb__btn--ghost fb__btn--xs" onClick={usePreset}>
            <FiFileText /> {t('flows.builder.usePreset')}
          </button>
        </div>

        {current.enabled === false ? (
          <p className="fb__email-disabled-hint">{t('flows.builder.emailDisabledHint')}</p>
        ) : (
          <>
            <div className="fb__field">
              <label className="fb__field-label">{t('flows.builder.emailSubject')}</label>
              <input
                className="fb__input"
                value={current.subject}
                onChange={e => patch({ subject: e.target.value })}
                placeholder={t('flows.builder.emailSubjectPlaceholder')}
              />
            </div>
            <div className="fb__field">
              <label className="fb__field-label">{t('flows.builder.emailBody')}</label>
              <textarea
                ref={bodyRef}
                className="fb__textarea"
                rows={5}
                value={current.body}
                onChange={e => patch({ body: e.target.value })}
                placeholder={t('flows.builder.emailBodyPlaceholder')}
              />
            </div>
            <div className="fb__placeholders">
              <span className="fb__placeholders-label">{t('flows.builder.placeholdersLabel')}</span>
              {EMAIL_PLACEHOLDERS.map(p => (
                <button key={p} type="button" className="fb__placeholder-chip" onClick={() => insertPlaceholder(p)}>
                  {`{{${p}}}`}
                </button>
              ))}
            </div>
            <p className="fb__email-default-hint"><FiInfo /> {t('flows.builder.emailDefaultHint')}</p>
          </>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Collapsible block — header (icon + title + hint) toggles the content.
// Pure UI state: collapsing only hides the inputs, form values live upstream.
// ─────────────────────────────────────────────────────────────────────────────
function CollapsibleBlock({ icon, tone, title, hint, open, onToggle, children }) {
  return (
    <div className={`fb__block fb__block--collapsible ${open ? 'is-open' : ''}`}>
      <button type="button" className="fb__block-toggle" onClick={onToggle} aria-expanded={open}>
        <span className={`fb__block-icon fb__block-icon--${tone}`}>{icon}</span>
        <span className="fb__block-toggle-texts">
          <h5>{title}</h5>
          <p>{hint}</p>
        </span>
        <FiChevronDown className="fb__block-chevron" />
      </button>
      {open && <div className="fb__block-content">{children}</div>}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Step card
// ─────────────────────────────────────────────────────────────────────────────
function StepCard({
  step, index, total, options, onPatch, onPatchEscalation, onPatchEmail,
  onMove, onDuplicate, onRemove, sectionRef, structureLocked = false,
}) {
  const { t, i18n } = useTranslation();
  const [collapsed, setCollapsed] = useState(false);
  // System steps (seeded standard workflow) cannot be deleted — only
  // toggled active/passive.
  const isSystem = !!step.systemKey;
  const stepActive = step.isActive !== false;
  // Legacy steps saved before the third type existed carry no `type`.
  const stepType = STEP_TYPES.some(s => s.value === step.type) ? step.type : 'approval';
  // Bulky sections start closed; opening/closing is UI-only, values are kept.
  const [timeoutOpen, setTimeoutOpen] = useState(false);
  const [emailsOpen, setEmailsOpen] = useState(false);

  const ESCALATION_ACTIONS = [
    { value: 'notify', icon: FiBell, tone: 'info' },
    { value: 'reassign', icon: FiCornerUpRight, tone: 'primary' },
    { value: 'approve', icon: FiCheckCircle, tone: 'success' },
    { value: 'reject', icon: FiXCircle, tone: 'danger' },
  ];

  return (
    <div className="fb__step" ref={sectionRef}>
      {/* Rail connector */}
      <div className="fb__step-rail">
        <div className="fb__step-num">{index + 1}</div>
        {index < total - 1 && <div className="fb__step-line" />}
      </div>

      <div className={`fb__step-card ${stepActive ? '' : 'fb__step-card--passive'}`}>
        <div className="fb__step-head">
          <button
            type="button"
            className={`fb__step-collapse ${collapsed ? '' : 'is-open'}`}
            onClick={() => setCollapsed(!collapsed)}
            title={collapsed ? t('flows.builder.expand') : t('flows.builder.collapse')}
          >
            <FiChevronRight />
          </button>
          <MultiLangInput
            value={step.name}
            onChange={(v) => onPatch({ name: v })}
            primaryLang={i18n.language}
            placeholder={t('flows.stepNamePlaceholder')}
            className="fb__step-name-ml"
            inputClassName="fb__step-name"
          />
          {isSystem && (
            <button
              type="button"
              className={`fb__step-active-toggle ${stepActive ? 'is-on' : ''}`}
              onClick={() => onPatch({ isActive: !stepActive })}
              title={t('flows.builder.stepActiveToggleHint')}
            >
              <span className="fb__step-active-dot" />
              {stepActive ? t('common.active') : t('common.inactive')}
            </button>
          )}
          {/* A standard workflow's step types are part of its fixed structure —
              the type is shown as a read-only badge instead of a picker. */}
          {structureLocked ? (
            <div className="fb__step-type fb__step-type--locked">
              {STEP_TYPES.filter(s => s.value === stepType).map(({ value, icon: TypeIcon }) => (
                <span
                  key={value}
                  className={`fb__step-type-btn is-active is-locked fb__step-type-btn--${value}`}
                  title={t('flows.builder.standardLockedHint')}
                >
                  <TypeIcon />
                  {t(`flows.stepTypes.${value}.label`)}
                  <FiLock className="fb__step-type-lock" />
                </span>
              ))}
            </div>
          ) : (
          <div className="fb__step-type">
            {STEP_TYPES.map(({ value, icon: TypeIcon }) => (
              <button
                key={value}
                type="button"
                className={`fb__step-type-btn ${stepType === value ? 'is-active' : ''} fb__step-type-btn--${value}`}
                onClick={() => {
                  if (stepType === value) return;
                  // Review steps start fully permissive; approval steps carry
                  // the same toggles but start all-off; information steps are
                  // always all-false (never handed the contract to edit).
                  // "Document actors" is an information-only rule — leaving
                  // the type behind drops it.
                  onPatch({
                    type: value,
                    reviewActions: reviewActionsAll(value === 'review'),
                    ...(value !== 'information' && step.assigneeOrgRule === DOC_ACTORS_ORG_RULE
                      ? { assigneeOrgRule: null } : {}),
                  });
                }}
              >
                <TypeIcon />
                {t(`flows.stepTypes.${value}.label`)}
              </button>
            ))}
          </div>
          )}
          {/* Reorder / copy / delete change the step *structure* — a standard
              workflow has none of them; its settings stay fully editable. */}
          {structureLocked ? (
            <span className="fb__step-locked" title={t('flows.builder.standardLockedHint')}>
              <FiLock /> {t('flows.builder.standardLockedBadge')}
            </span>
          ) : (
          <div className="fb__step-actions">
            <button type="button" className="fb__icon-btn" disabled={index === 0} onClick={() => onMove(-1)} title={t('flows.builder.moveUp')}><FiChevronUp /></button>
            <button type="button" className="fb__icon-btn" disabled={index === total - 1} onClick={() => onMove(1)} title={t('flows.builder.moveDown')}><FiChevronDown /></button>
            <button type="button" className="fb__icon-btn" onClick={onDuplicate} title={t('flows.builder.duplicate')}><FiCopy /></button>
            <button
              type="button"
              className="fb__icon-btn fb__icon-btn--danger"
              disabled={total <= 1 || isSystem}
              onClick={onRemove}
              title={isSystem ? t('flows.messages.systemStepNoDelete') : t('flows.builder.remove')}
            >
              <FiTrash2 />
            </button>
          </div>
          )}
        </div>

        {/* Collapsed summary */}
        {collapsed ? (
          <div className="fb__step-summary" onClick={() => setCollapsed(false)}>
            <span className={`fb__pill fb__pill--type-${stepType}`}>
              {t(`flows.stepTypes.${stepType}.label`)}
            </span>
            {stepType !== 'information' && (
              <>
                <span className="fb__pill"><FiClock /> {formatDuration(step.timeoutMinutes, t)}</span>
                <span className="fb__pill">
                  {t(`flows.builder.escalationActions.${step.escalation.action}.label`)}
                </span>
              </>
            )}
          </div>
        ) : (
          <div className="fb__step-body">
            {/* What the selected type means — the first thing in the step
                configuration, so the designer sees the consequence of the
                choice they just made. */}
            <div className={`fb__step-typeinfo fb__step-typeinfo--${stepType}`}>
              <FiInfo />
              <p>{t(`flows.stepTypes.${stepType}.info`)}</p>
            </div>

            {/* Assignee */}
            <div className="fb__block">
              <div className="fb__block-head">
                <span className="fb__block-icon fb__block-icon--primary"><FiUser /></span>
                <div>
                  <h5>{stepType === 'information'
                    ? t('flows.builder.recipientTitle')
                    : t('flows.builder.approverTitle')}</h5>
                  <p>{stepType === 'information'
                    ? t('flows.builder.recipientHint')
                    : t('flows.builder.approverHint')}</p>
                </div>
              </div>
              <AssigneePicker
                value={step}
                onChange={onPatch}
                options={options}
                allowOrganization
                orgRules={stepType === 'information'
                  ? [...ORG_RULE_KEYS, DOC_ACTORS_ORG_RULE]
                  : ORG_RULE_KEYS}
              />

              {/* Completion policy — only for assignee types that can resolve
                  to several people (group / title). All = everyone decides
                  (legacy default); quorum = N approvals complete the step and
                  release the rest. */}
              {stepType !== 'information' && ['group', 'title'].includes(step.assigneeType) && (
                <div className="fb__quorum">
                  <span className="fb__field-label">{t('flows.builder.approvalPolicyLabel')}</span>
                  <div className="fb__quorum-options">
                    <label className={`fb__quorum-option ${step.approvalPolicy !== 'quorum' ? 'is-on' : ''}`}>
                      <input
                        type="radio"
                        name={`policy-${step._key}`}
                        checked={step.approvalPolicy !== 'quorum'}
                        onChange={() => onPatch({ approvalPolicy: 'all' })}
                      />
                      <span>
                        <strong>{t('flows.builder.approvalPolicyAll')}</strong>
                        <em>{t('flows.builder.approvalPolicyAllDesc')}</em>
                      </span>
                    </label>
                    <label className={`fb__quorum-option ${step.approvalPolicy === 'quorum' ? 'is-on' : ''}`}>
                      <input
                        type="radio"
                        name={`policy-${step._key}`}
                        checked={step.approvalPolicy === 'quorum'}
                        onChange={() => onPatch({ approvalPolicy: 'quorum' })}
                      />
                      <span>
                        <strong>{t('flows.builder.approvalPolicyQuorum')}</strong>
                        <em>{t('flows.builder.approvalPolicyQuorumDesc')}</em>
                      </span>
                    </label>
                  </div>
                  {step.approvalPolicy === 'quorum' && (
                    <div className="fb__quorum-count">
                      <label>{t('flows.builder.quorumCountLabel')}</label>
                      <input
                        type="number"
                        min={1}
                        max={99}
                        value={step.quorumCount ?? 1}
                        onChange={e => onPatch({
                          quorumCount: Math.max(1, Math.min(99, Math.round(Number(e.target.value) || 1))),
                        })}
                      />
                      <span className="fb__quorum-hint">{t('flows.builder.quorumCountHint')}</span>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Reviewer/approver/checker actions — review steps default
                all-on, control and approval steps carry the same toggles but
                default all-off */}
            {['review', 'control', 'approval'].includes(stepType) && (
              <div className="fb__block">
                <div className="fb__block-head">
                  <span className="fb__block-icon fb__block-icon--success"><FiShield /></span>
                  <div>
                    <h5>{stepType === 'approval' ? t('flows.builder.approvalActionsTitle')
                      : stepType === 'control' ? t('flows.builder.controlActionsTitle')
                      : t('flows.builder.reviewActionsTitle')}</h5>
                    <p>{stepType === 'approval' ? t('flows.builder.approvalActionsHint')
                      : stepType === 'control' ? t('flows.builder.controlActionsHint')
                      : t('flows.builder.reviewActionsHint')}</p>
                  </div>
                </div>
                <div className="fb__ra-grid">
                  {REVIEW_ACTION_KEYS.map(key => (
                    <label key={key} className={`fb__ra-item ${step.reviewActions?.[key] ? 'is-on' : ''}`}>
                      <span className="fb__switch">
                        <input
                          type="checkbox"
                          checked={step.reviewActions?.[key] === true}
                          onChange={e => onPatch({
                            reviewActions: { ...reviewActionsAll(false), ...step.reviewActions, [key]: e.target.checked },
                          })}
                        />
                        <span className="fb__switch-track"><span className="fb__switch-thumb" /></span>
                      </span>
                      <span className="fb__ra-texts">
                        <span className="fb__ra-label">{t(`flows.builder.reviewActionsItems.${key}.label`)}</span>
                        <span className="fb__ra-desc">{t(`flows.builder.reviewActionsItems.${key}.desc`)}</span>
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Görüş Al / Yönlendir — per-step abilities of the action owner.
                Not tied to roles: the designer decides where each is offered.
                Information steps ask nothing of anyone, so neither applies. */}
            {stepType !== 'information' && (
              <div className="fb__block">
                <div className="fb__block-head">
                  <span className="fb__block-icon fb__block-icon--info"><FiMessageSquare /></span>
                  <div>
                    <h5>{t('flows.builder.stepAbilitiesTitle')}</h5>
                    <p>{t('flows.builder.stepAbilitiesHint')}</p>
                  </div>
                </div>
                <div className="fb__ra-grid">
                  {STEP_ABILITY_KEYS.map(key => (
                    <label key={key} className={`fb__ra-item ${step[key] ? 'is-on' : ''}`}>
                      <span className="fb__switch">
                        <input
                          type="checkbox"
                          checked={step[key] === true}
                          onChange={e => onPatch({ [key]: e.target.checked })}
                        />
                        <span className="fb__switch-track"><span className="fb__switch-thumb" /></span>
                      </span>
                      <span className="fb__ra-texts">
                        <span className="fb__ra-label">{t(`flows.builder.stepAbilitiesItems.${key}.label`)}</span>
                        <span className="fb__ra-desc">{t(`flows.builder.stepAbilitiesItems.${key}.desc`)}</span>
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Timeout + what happens when it runs out (collapsible).
                Hidden for information steps: nothing is awaited, so there is
                no deadline to miss and nothing to escalate. */}
            {stepType !== 'information' && (
            <CollapsibleBlock
              icon={<FiClock />}
              tone="warning"
              title={t('flows.builder.timeoutTitle')}
              hint={t('flows.builder.timeoutHint')}
              open={timeoutOpen}
              onToggle={() => setTimeoutOpen(o => !o)}
            >
              <TimeoutComposer
                minutes={step.timeoutMinutes}
                onChange={(m) => onPatch({ timeoutMinutes: m })}
              />

              <div className="fb__block-subhead">
                <h6>{t('flows.builder.escalationTitle')}</h6>
                <p>{t('flows.builder.escalationHint')}</p>
              </div>
              <div className="fb__esc-grid">
                {ESCALATION_ACTIONS.map(a => {
                  const Icon = a.icon;
                  const active = step.escalation.action === a.value;
                  return (
                    <button
                      key={a.value}
                      type="button"
                      className={`fb__esc-card fb__esc-card--${a.tone} ${active ? 'is-active' : ''}`}
                      onClick={() => onPatchEscalation({ action: a.value })}
                    >
                      <span className="fb__esc-card-icon"><Icon /></span>
                      <span className="fb__esc-card-title">{t(`flows.builder.escalationActions.${a.value}.label`)}</span>
                      <span className="fb__esc-card-desc">{t(`flows.builder.escalationActions.${a.value}.desc`)}</span>
                      {active && <span className="fb__esc-card-check"><FiCheck /></span>}
                    </button>
                  );
                })}
              </div>
              {step.escalation.action === 'reassign' && (
                <div className="fb__esc-target">
                  <label className="fb__field-label">{t('flows.builder.escalationTargetLabel')}</label>
                  <AssigneePicker
                    value={step.escalation}
                    onChange={onPatchEscalation}
                    options={options}
                    compact
                  />
                </div>
              )}
            </CollapsibleBlock>
            )}

            {/* Email templates (collapsible) */}
            <CollapsibleBlock
              icon={<FiMail />}
              tone="info"
              title={t('flows.builder.emailsTitle')}
              hint={stepType === 'information'
                ? t('flows.builder.emailsInformationHint')
                : t('flows.builder.emailsHint')}
              open={emailsOpen}
              onToggle={() => setEmailsOpen(o => !o)}
            >
              <EmailTemplateEditor
                emails={step.emails}
                onChange={onPatchEmail}
                escalationEnabled={step.escalation.action === 'reassign'}
                informationOnly={stepType === 'information'}
              />
            </CollapsibleBlock>
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main page
// ─────────────────────────────────────────────────────────────────────────────
function FlowBuilder() {
  const { t, i18n } = useTranslation();
  // Value limits are expressed in the tenant's reporting currency — the same
  // currency contract amounts are converted into before being compared, so the
  // label has to say which one it is.
  const org = useSelector((s) => s.settings?.config?.organization);
  const reportingCurrency = (org?.reportingCurrency || org?.defaultCurrency || 'USD').toUpperCase();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;

  // Scope authority: non-admins create workflows pinned to their own company
  // ("All Companies" is reserved for owners/admins).
  const authUser = useSelector((s) => s.auth?.user || null);
  const scopePrivileged = canScopeAllCompanies(authUser);
  const defaultScopeIds = defaultScopeForUser(authUser);

  const [form, setForm] = useState(() => {
    const wf = emptyWorkflow();
    return { ...wf, steps: withKeys(wf.steps) };
  });

  // New workflow: start from the creator's default scope. (The auth user is
  // already in the store on mount; the guard keeps a later store refresh from
  // overwriting a scope the user has meanwhile touched.)
  useEffect(() => {
    if (isEdit || !defaultScopeIds.length) return;
    setForm(prev => ((prev.companyIds || []).length ? prev : { ...prev, companyIds: [...defaultScopeIds] }));
    // eslint-disable-next-line
  }, [isEdit, authUser]);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);

  // Live runs of this definition. Fetched only when a save is refused because
  // of them, then shown in a popup where the tenant owner can kill them.
  const [activeInstances, setActiveInstances] = useState([]);
  const [showActiveRuns, setShowActiveRuns] = useState(false);
  const [killTarget, setKillTarget] = useState(null);
  const [killing, setKilling] = useState(false);
  const isTenantOwner = authUser?.role === 'tenant_owner';

  const [wfPositions, setWfPositions] = useState([]);
  const [wfTitles, setWfTitles] = useState([]);
  const [wfUsers, setWfUsers] = useState([]);
  const [wfGroups, setWfGroups] = useState([]);
  const [wfCompanies, setWfCompanies] = useState([]);

  const stepRefs = useRef({});

  // ── Load option lists + (edit) workflow ──
  useEffect(() => {
    Promise.all([
      positionsAPI.list({ active: true }),
      titlesAPI.list({ active: true }),
      usersAPI.list({ limit: 200 }),
      groupsAPI.list(),
      companiesAPI.list(),
    ]).then(([pos, tit, usr, grp, comp]) => {
      setWfPositions(pos.data.positions || []);
      setWfTitles(tit.data.titles || []);
      setWfUsers(usr.data.users || []);
      setWfGroups(grp.data.groups || []);
      setWfCompanies(comp.data.companies || []);
    }).catch(() => {});

    if (isEdit) {
      workflowsAPI.get(id)
        .then(({ data }) => {
          const f = workflowToForm(data.workflow);
          setForm({ ...f, steps: withKeys(f.steps) });
        })
        .catch(() => {
          toast.error(t('flows.messages.loadFailed'));
          navigate('/flow-management');
        })
        .finally(() => setLoading(false));
    }
  }, [id, isEdit, navigate, t]);

  // Mirror of the server's save-time grant: the draft-gating Control step's
  // assignee gets the document actions (they must be able to attach the
  // draft), so the step's checkboxes show what will actually be saved.
  const grantDraftActions = (steps, stepKey) => steps.map(s => (s.stepKey === stepKey
    ? {
      ...s,
      reviewActions: {
        ...reviewActionsAll(false),
        ...s.reviewActions,
        canAddDocuments: true,
        canEditDocuments: true,
        canUploadVersions: true,
      },
    }
    : s));

  const setDraftReqStep = (stepKey) => setForm(f => ({
    ...f,
    draftRequirement: { mode: 'at_control_step', stepKey: stepKey || null },
    steps: stepKey ? grantDraftActions(f.steps, stepKey) : f.steps,
  }));

  const killInstance = async () => {
    if (!killTarget) return;
    setKilling(true);
    try {
      await workflowsAPI.killInstance(killTarget._id);
      const remaining = activeInstances.filter((i) => i._id !== killTarget._id);
      setActiveInstances(remaining);
      setKillTarget(null);
      if (remaining.length === 0) {
        // Nothing blocks the save anymore — close the popup and say so.
        setShowActiveRuns(false);
        toast.success(t('flows.builder.activeInstancesCleared'));
      } else {
        toast.success(t('flows.messages.instanceKilled'));
      }
    } catch (err) {
      toast.error(err.response?.data?.code === 'OWNER_ONLY'
        ? t('flows.builder.activeInstancesOwnerOnly')
        : err.response?.data?.error || t('flows.messages.instanceKillFailed'));
    } finally {
      setKilling(false);
    }
  };

  // ── Options for the pickers ──
  const options = useMemo(() => ({
    position: wfPositions.map(p => ({
      value: p._id,
      label: `${p.name} (${p.code})`,
      sublabel: p.assignedUser
        ? `${p.assignedUser.firstName} ${p.assignedUser.lastName}`
        : t('flows.builder.unassigned'),
    })),
    title: wfTitles.map(ti => ({
      value: ti._id,
      label: `${ti.name} (${ti.code})`,
      sublabel: t('flows.builder.userCount', { count: ti.userCount ?? 0 }),
    })),
    // Active users are pickable. A deactivated user stays in the list only
    // while a step (or an escalation) still references them — labelled as
    // inactive — so the current assignment never renders as an empty box.
    user: (() => {
      const referenced = new Set();
      for (const s of form.steps || []) {
        if (s.assigneeUser) referenced.add(String(s.assigneeUser));
        if (s.escalation?.assigneeUser) referenced.add(String(s.escalation.assigneeUser));
      }
      return wfUsers
        .filter(u => u.isActive || referenced.has(String(u._id)))
        .map(u => ({
          value: u._id,
          label: u.isActive
            ? `${u.firstName} ${u.lastName}`
            : `${u.firstName} ${u.lastName} — ${t('common.inactive')}`,
          sublabel: u.email,
        }));
    })(),
    group: wfGroups.map(g => ({
      value: g._id,
      label: groupName(g, t),
      sublabel: t('flows.builder.memberCount', { count: g.members?.length ?? 0 }),
    })),
  }), [wfPositions, wfTitles, wfUsers, wfGroups, form.steps, t]);

  // ── Form mutation helpers ──
  const patchForm = (p) => setForm(f => ({ ...f, ...p }));
  const patchTrigger = (p) => setForm(f => ({ ...f, triggerConditions: { ...f.triggerConditions, ...p } }));

  const patchStep = (idx, p) => setForm(f => {
    const steps = [...f.steps];
    steps[idx] = { ...steps[idx], ...p };
    return { ...f, steps };
  });
  const patchStepEscalation = (idx, p) => setForm(f => {
    const steps = [...f.steps];
    steps[idx] = { ...steps[idx], escalation: { ...steps[idx].escalation, ...p } };
    return { ...f, steps };
  });
  const patchStepEmail = (idx, kind, p) => setForm(f => {
    const steps = [...f.steps];
    steps[idx] = {
      ...steps[idx],
      emails: { ...steps[idx].emails, [kind]: { ...steps[idx].emails[kind], ...p } },
    };
    return { ...f, steps };
  });

  // A standard (SYSTEM) workflow ships a fixed step structure: its steps can be
  // configured freely, but none may be added, copied, deleted, retyped or
  // reordered. Anything beyond that means a custom workflow (copy it first).
  const structureLocked = !!form.isStandard;

  const addStep = () => setForm(f => {
    if (f.isStandard) return f;
    return { ...f, steps: [...f.steps, { ...emptyStep(f.steps.length + 1), _key: nextStepKey() }] };
  });

  const moveStep = (idx, dir) => setForm(f => {
    if (f.isStandard) return f;
    const steps = [...f.steps];
    const target = idx + dir;
    if (target < 0 || target >= steps.length) return f;
    [steps[idx], steps[target]] = [steps[target], steps[idx]];
    return { ...f, steps: steps.map((s, i) => ({ ...s, order: i + 1 })) };
  });

  const duplicateStep = (idx) => setForm(f => {
    if (f.isStandard) return f;
    const steps = [...f.steps];
    const copy = JSON.parse(JSON.stringify({ ...steps[idx], _key: undefined }));
    copy._key = nextStepKey();
    // The copy gets its own identity — the original keeps the stepKey any
    // draft-requirement reference points at.
    copy.stepKey = genStepKey();
    // Copies are plain user steps: freely editable and deletable
    copy.systemKey = null;
    copy.isActive = true;
    steps.splice(idx + 1, 0, copy);
    return { ...f, steps: steps.map((s, i) => ({ ...s, order: i + 1 })) };
  });

  const removeStep = (idx) => setForm(f => {
    if (f.isStandard) {
      toast.error(t('flows.messages.standardStructureLocked'));
      return f;
    }
    if (f.steps[idx]?.systemKey) {
      toast.error(t('flows.messages.systemStepNoDelete'));
      return f;
    }
    if (f.steps.length <= 1) {
      toast.error(t('flows.messages.minOneStep'));
      return f;
    }
    const steps = f.steps.filter((_, i) => i !== idx).map((s, i) => ({ ...s, order: i + 1 }));
    return { ...f, steps };
  });

  // ── Save ──
  const save = async () => {
    if (!localizeSeeded(form.name, i18n.language).trim()) { toast.error(t('flows.messages.workflowNameRequired')); return; }
    if (form.steps.some(s => !localizeSeeded(s.name, i18n.language).trim())) { toast.error(t('flows.messages.stepNameRequired')); return; }
    const badOrgStep = form.steps.findIndex(s => s.assigneeType === 'organization' && !s.assigneeOrgRule);
    if (badOrgStep !== -1) {
      toast.error(t('flows.messages.orgRuleRequired', { n: badOrgStep + 1 }));
      stepRefs.current[badOrgStep]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    // Information steps have no escalation UI (nothing can time out), so a
    // stale escalation left on one must not block the save.
    const badEscalation = form.steps.findIndex(
      s => s.type !== 'information'
        && s.escalation.action === 'reassign' && !hasEscalationTarget(s.escalation)
    );
    if (badEscalation !== -1) {
      toast.error(t('flows.builder.messages.escalationTargetRequired', { n: badEscalation + 1 }));
      stepRefs.current[badEscalation]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    // Taslak Zorunluluğu: a deferred requirement must point at an existing,
    // active Control step — deleting/passivating/retyping the referenced step
    // (or never picking one) blocks the save until it is resolved.
    if (form.draftRequirement?.mode === 'at_control_step') {
      const target = form.steps.find(s => s.stepKey === form.draftRequirement.stepKey);
      if (!target || target.type !== 'control' || target.isActive === false) {
        toast.error(t('flows.builder.messages.draftReqStepRequired'));
        document.getElementById('fb-details')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
    }

    setSaving(true);
    try {
      const payload = {
        name: typeof form.name === 'string' ? form.name.trim() : form.name,
        description: form.description,
        // Company scope: [] = All Companies, ids = specific companies only.
        companyIds: form.companyIds || [],
        // Only value limits remain configurable; clear legacy type/department
        // conditions so saved workflows match what the form shows.
        triggerConditions: {
          contractTypes: [],
          departments: [],
          minValue: form.triggerConditions.minValue !== '' ? Number(form.triggerConditions.minValue) : undefined,
          maxValue: form.triggerConditions.maxValue !== '' ? Number(form.triggerConditions.maxValue) : undefined,
        },
        allowExternalSendOnReturn: form.allowExternalSendOnReturn === true,
        draftRequirement: form.draftRequirement?.mode === 'at_control_step'
          ? { mode: 'at_control_step', stepKey: form.draftRequirement.stepKey }
          : { mode: 'at_start', stepKey: null },
        steps: form.steps.map((s, i) => ({
          order: i + 1,
          stepKey: s.stepKey,
          name: typeof s.name === 'string' ? s.name.trim() : s.name,
          type: s.type,
          systemKey: s.systemKey || null,
          isActive: s.isActive !== false,
          assigneeType: s.assigneeType,
          assigneePosition: s.assigneePosition,
          assigneeTitle: s.assigneeTitle,
          assigneeUser: s.assigneeUser,
          assigneeGroup: s.assigneeGroup,
          assigneeOrgRule: s.assigneeType === 'organization' ? s.assigneeOrgRule : null,
          timeoutMinutes: clampTimeout(s.timeoutMinutes),
          escalation: s.escalation,
          emails: s.emails,
          reviewActions: ['review', 'control', 'approval'].includes(s.type) ? s.reviewActions : reviewActionsAll(false),
          allowOpinion: s.type !== 'information' && s.allowOpinion === true,
          allowForward: s.type !== 'information' && s.allowForward === true,
          allowExternalSend: s.type !== 'information' && s.allowExternalSend === true,
          // Quorum only makes sense for multi-assignee types; anything else
          // saves as the default 'all'.
          approvalPolicy: ['group', 'title'].includes(s.assigneeType) && s.approvalPolicy === 'quorum' ? 'quorum' : 'all',
          quorumCount: Math.max(1, Number(s.quorumCount) || 1),
        })),
      };

      if (isEdit) {
        await workflowsAPI.update(id, payload);
        toast.success(t('flows.messages.workflowUpdated'));
      } else {
        await workflowsAPI.create(payload);
        toast.success(t('flows.messages.workflowCreated'));
      }
      navigate('/flow-management');
    } catch (err) {
      // The structure guard is the server's last word on a standard workflow —
      // show it in the user's language rather than the raw English reason.
      const code = err.response?.data?.code;
      if (code === 'WORKFLOW_HAS_ACTIVE_INSTANCES') {
        // The save was refused because runs are in flight: fetch them now and
        // open the popup where the tenant owner can delete them.
        try {
          const { data } = await workflowsAPI.activeInstances(id);
          setActiveInstances(data.instances || []);
          setShowActiveRuns(true);
        } catch {
          toast.error(t('flows.messages.workflowHasActiveInstances'));
        }
        return;
      }
      toast.error(
        ['STANDARD_STRUCTURE_LOCKED', 'STANDARD_STEP_DELETE'].includes(code)
          ? t('flows.messages.standardStructureLocked')
          : err.response?.data?.message || err.response?.data?.error || t('flows.messages.workflowSaveFailed')
      );
    } finally {
      setSaving(false);
    }
  };

  const totalDuration = useMemo(
    () => form.steps.reduce((sum, s) => sum + (Number(s.timeoutMinutes) || 0), 0),
    [form.steps]
  );

  if (loading) {
    return (
      <div className="fb fb--loading">
        <div className="fb__loading-spinner"><FiRefreshCw /></div>
      </div>
    );
  }

  return (
    <div className="fb">
      {/* ── Sticky top bar ── */}
      <div className="fb__topbar">
        <div className="fb__topbar-left">
          <button className="fb__back" onClick={() => navigate('/flow-management')} title={t('flows.builder.backToList')}>
            <FiArrowLeft />
          </button>
          <div className="fb__topbar-titles">
            <span className="fb__topbar-eyebrow"><FiGitBranch /> {t('flows.eyebrow')}</span>
            <h1 className="fb__topbar-title">
              {isEdit ? t('flows.builder.editTitle') : t('flows.builder.createTitle')}
              {form.name && <span className="fb__topbar-name">— {localizeSeeded(form.name, i18n.language)}</span>}
            </h1>
          </div>
        </div>
        <div className="fb__topbar-actions">
          <button className="fb__btn fb__btn--ghost" onClick={() => navigate('/flow-management')}>
            <FiX /> {t('common.cancel')}
          </button>
          <button className="fb__btn fb__btn--primary" onClick={save} disabled={saving}>
            {saving
              ? <><FiRefreshCw className="fb-spin" /> {t('common.saving')}</>
              : <><FiCheck /> {isEdit ? t('flows.builder.saveUpdate') : t('flows.builder.saveCreate')}</>}
          </button>
        </div>
      </div>

      <div className="fb__layout">
        {/* ── Sticky outline rail ── */}
        <aside className="fb__rail">
          <nav className="fb__rail-nav">
            <a href="#fb-details" className="fb__rail-link"><FiInfo /> {t('flows.builder.navDetails')}</a>
            <a href="#fb-conditions" className="fb__rail-link"><FiSliders /> {t('flows.builder.navLimits')}</a>
            <a href="#fb-steps" className="fb__rail-link"><FiLayers /> {t('flows.builder.navSteps')}</a>
          </nav>
          <div className="fb__rail-steps">
            {form.steps.map((s, i) => (
              <button
                key={s._key}
                type="button"
                className="fb__rail-step"
                onClick={() => stepRefs.current[i]?.scrollIntoView({ behavior: 'smooth', block: 'center' })}
              >
                <span className="fb__rail-step-num">{i + 1}</span>
                <span className="fb__rail-step-name">{localizeSeeded(s.name, i18n.language) || t('flows.stepNumber', { n: i + 1 })}</span>
              </button>
            ))}
            {!structureLocked && (
              <button type="button" className="fb__rail-add" onClick={addStep}>
                <FiPlus /> {t('flows.addStep')}
              </button>
            )}
          </div>
          <div className="fb__rail-total">
            <FiClock />
            <div>
              <span className="fb__rail-total-label">{t('flows.builder.totalDuration')}</span>
              <span className="fb__rail-total-value">{formatDuration(totalDuration, t)}</span>
            </div>
          </div>
        </aside>

        {/* ── Main column ── */}
        <main className="fb__main">
          {/* Details */}
          <section id="fb-details" className="fb__section">
            <div className="fb__section-head">
              <h3><FiInfo /> {t('flows.builder.detailsTitle')}</h3>
              <p>{t('flows.builder.detailsHint')}</p>
            </div>
            <div className="fb__section-body">
              {/* Scope first — the standard for definition screens: pick the
                  company scope, the rest of the form follows it. The compact
                  bar carries its own label. */}
              <div className="fb__field">
                <CompanyScopeSelect
                  value={form.companyIds || []}
                  onChange={(ids) => patchForm({ companyIds: ids })}
                  companies={wfCompanies}
                  restrictToCompanyIds={scopePrivileged ? null : defaultScopeIds}
                />
                <p className="fb__hint">{t('companyScope.workflowHint')}</p>
              </div>
              <div className="fb__field">
                <label className="fb__field-label">{t('flows.workflowName')} <span className="fb__required">*</span></label>
                <MultiLangInput
                  value={form.name}
                  onChange={(v) => patchForm({ name: v })}
                  primaryLang={i18n.language}
                  placeholder={t('flows.workflowNamePlaceholder')}
                  inputClassName="fb__input fb__input--lg"
                />
              </div>
              <div className="fb__field">
                <label className="fb__field-label">{t('common.description')}</label>
                <MultiLangInput
                  value={form.description}
                  onChange={(v) => patchForm({ description: v })}
                  primaryLang={i18n.language}
                  multiline
                  rows={2}
                  placeholder={t('flows.workflowDescPlaceholder')}
                  inputClassName="fb__textarea"
                />
              </div>

              {/* Taslak Zorunluluğu — when the draft document becomes mandatory */}
              {(() => {
                const draftReq = form.draftRequirement || { mode: 'at_start', stepKey: null };
                const controlSteps = form.steps
                  .map((s, i) => ({ ...s, _idx: i }))
                  .filter(s => s.type === 'control' && s.isActive !== false);
                const deferred = draftReq.mode === 'at_control_step';
                const target = deferred ? controlSteps.find(s => s.stepKey === draftReq.stepKey) : null;
                return (
                  <div className="fb__field">
                    <label className="fb__field-label">{t('flows.builder.draftReqTitle')}</label>
                    <div className="fb__org-rules">
                      <button
                        type="button"
                        className={`fb__org-rule ${!deferred ? 'is-active' : ''}`}
                        onClick={() => patchForm({ draftRequirement: { mode: 'at_start', stepKey: null } })}
                      >
                        <span className="fb__org-rule-radio" aria-hidden="true" />
                        <span className="fb__org-rule-texts">
                          <span className="fb__org-rule-label">{t('flows.builder.draftReqAtStart')}</span>
                          <span className="fb__org-rule-desc">{t('flows.builder.draftReqAtStartDesc')}</span>
                        </span>
                      </button>
                      <button
                        type="button"
                        disabled={!controlSteps.length}
                        className={`fb__org-rule ${deferred ? 'is-active' : ''}`}
                        onClick={() => controlSteps.length && setDraftReqStep(
                          // A single Control step needs no picking — preselect it.
                          draftReq.stepKey
                            || (controlSteps.length === 1 ? controlSteps[0].stepKey : null)
                        )}
                      >
                        <span className="fb__org-rule-radio" aria-hidden="true" />
                        <span className="fb__org-rule-texts">
                          <span className="fb__org-rule-label">{t('flows.builder.draftReqAtControl')}</span>
                          <span className="fb__org-rule-desc">{t('flows.builder.draftReqAtControlDesc')}</span>
                        </span>
                      </button>
                    </div>
                    {!controlSteps.length && !deferred && (
                      <p className="fb__hint">{t('flows.builder.draftReqNoControl')}</p>
                    )}
                    {deferred && (
                      <div className="fb__field" style={{ marginTop: '0.6rem' }}>
                        <label className="fb__field-label">
                          {t('flows.builder.draftReqStepLabel')} <span className="fb__required">*</span>
                        </label>
                        <SearchableSelect
                          value={draftReq.stepKey || ''}
                          onChange={(v) => setDraftReqStep(v)}
                          options={controlSteps.map(s => ({
                            value: s.stepKey,
                            label: localizeSeeded(s.name, i18n.language) || `${t('flows.builder.stepBadge', { defaultValue: 'Step' })} ${s._idx + 1}`,
                            sublabel: `#${s._idx + 1}`,
                          }))}
                          placeholder={t('flows.builder.draftReqStepPlaceholder')}
                        />
                        <p className="fb__hint">{t('flows.builder.draftReqStepInfo')}</p>
                        <p className="fb__hint">{t('flows.builder.draftReqAutoGrant')}</p>
                        {!target && (
                          <p className="fb__hint fb__hint--warn">
                            {controlSteps.length
                              ? t('flows.builder.draftReqInvalidWarn')
                              : t('flows.builder.draftReqNoControl')}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Geri Gönderimde Başlatana Harici Gönder İzni — a flow-level
                  setting, deliberately separate from the per-step Harici
                  Gönder ability: that one governs whoever owns a running
                  step, this one governs the initiator once the contract has
                  been sent back to them. With both off the feature is simply
                  not available in this flow. */}
              <div className="fb__field">
                <label className="fb__field-label">{t('flows.builder.externalOnReturnTitle')}</label>
                <div className="fb__ra-grid">
                  <label className={`fb__ra-item ${form.allowExternalSendOnReturn ? 'is-on' : ''}`}>
                    <span className="fb__switch">
                      <input
                        type="checkbox"
                        checked={form.allowExternalSendOnReturn === true}
                        onChange={e => patchForm({ allowExternalSendOnReturn: e.target.checked })}
                      />
                      <span className="fb__switch-track"><span className="fb__switch-thumb" /></span>
                    </span>
                    <span className="fb__ra-texts">
                      <span className="fb__ra-label">{t('flows.builder.externalOnReturnLabel')}</span>
                      <span className="fb__ra-desc">{t('flows.builder.externalOnReturnDesc')}</span>
                    </span>
                  </label>
                </div>
              </div>
            </div>
          </section>

          {/* Limits — contract value range */}
          <section id="fb-conditions" className="fb__section">
            <div className="fb__section-head">
              <h3><FiSliders /> {t('flows.builder.limitsTitle')}</h3>
              <p>{t('flows.builder.limitsHint')}</p>
            </div>
            <div className="fb__section-body">
              <div className="fb__grid-2">
                <div className="fb__field">
                  <label className="fb__field-label">
                    {t('flows.minContractValue')} <span className="fb__field-unit">({reportingCurrency})</span>
                  </label>
                  <input
                    type="number"
                    className="fb__input"
                    value={form.triggerConditions.minValue}
                    onChange={e => patchTrigger({ minValue: e.target.value })}
                    placeholder="50000"
                  />
                </div>
                <div className="fb__field">
                  <label className="fb__field-label">
                    {t('flows.maxContractValue')} <span className="fb__field-unit">({reportingCurrency})</span>
                  </label>
                  <input
                    type="number"
                    className="fb__input"
                    value={form.triggerConditions.maxValue}
                    onChange={e => patchTrigger({ maxValue: e.target.value })}
                    placeholder="500000"
                  />
                </div>
              </div>
              <p className="fb__hint">{t('flows.builder.limitsEmptyHint')}</p>
            </div>
          </section>

          {/* Steps */}
          <section id="fb-steps" className="fb__section fb__section--steps">
            <div className="fb__section-head">
              <h3><FiLayers /> {t('flows.builder.stepsTitle', { count: form.steps.length })}</h3>
              <p>{t('flows.builder.stepsHint')}</p>
            </div>
            {structureLocked && (
              <div className="fb__locked-note">
                <span className="fb__locked-note-icon"><FiLock /></span>
                <div className="fb__locked-note-texts">
                  <h5>{t('flows.builder.standardLockedTitle')}</h5>
                  <p>{t('flows.builder.standardLockedDesc')}</p>
                </div>
              </div>
            )}
            <div className="fb__steps">
              {form.steps.map((step, idx) => (
                <StepCard
                  key={step._key}
                  step={step}
                  index={idx}
                  total={form.steps.length}
                  options={options}
                  onPatch={(p) => patchStep(idx, p)}
                  onPatchEscalation={(p) => patchStepEscalation(idx, p)}
                  onPatchEmail={(kind, p) => patchStepEmail(idx, kind, p)}
                  onMove={(dir) => moveStep(idx, dir)}
                  onDuplicate={() => duplicateStep(idx)}
                  onRemove={() => removeStep(idx)}
                  sectionRef={(el) => { stepRefs.current[idx] = el; }}
                  structureLocked={structureLocked}
                />
              ))}
              {!structureLocked && (
                <button type="button" className="fb__add-step" onClick={addStep}>
                  <FiPlus /> {t('flows.addStep')}
                </button>
              )}

              {/* System default: every approval run automatically ends with
                  the signed-contract task for the initiator — not a step the
                  user manages, so it is shown as a fixed system endnote. */}
              <div className="fb__system-endnote">
                <span className="fb__system-endnote-icon"><FiUploadCloud /></span>
                <div className="fb__system-endnote-texts">
                  <span className="fb__system-endnote-title">
                    {t('flows.signedStep.defaultName', 'İmzalı Sözleşme')}
                    <em> — {t('flows.signedStep.assigneeLabel')}</em>
                  </span>
                  <p>{t('flows.signedStep.assigneeDesc')} {t('flows.signedStep.conditionalHint')}</p>
                </div>
                <FiLock className="fb__system-endnote-lock" />
              </div>
            </div>
          </section>
        </main>
      </div>

      {/* Popup shown when a save was refused because runs are in flight.
          Everyone sees the list; only the tenant owner gets the kill action. */}
      {showActiveRuns && (
        <div className="fb__runs-overlay" onClick={() => setShowActiveRuns(false)}>
          <div className="fb__runs-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <div className="fb__runs-head">
              <div className="fb__runs-head-text">
                <h3><FiAlertTriangle /> {t('flows.builder.activeInstancesTitle')}</h3>
                <p>{t('flows.builder.activeInstancesHint')}</p>
              </div>
              <button type="button" className="fb__runs-close" onClick={() => setShowActiveRuns(false)} aria-label={t('common.cancel')}>
                <FiX size={18} />
              </button>
            </div>
            <div className="fb__runs-body">
              {activeInstances.length === 0 ? (
                <p className="fb__hint">{t('flows.builder.activeInstancesCleared')}</p>
              ) : (
                <div className="fb__active-list">
                  {activeInstances.map((inst) => (
                    <div key={inst._id} className="fb__active-row">
                      <div className="fb__active-info">
                        <span className="fb__active-contract">
                          {inst.contract?.name || '—'}
                          {inst.contract?.number ? ` · ${inst.contract.number}` : ''}
                        </span>
                        <span className="fb__active-meta">
                          {inst.startedBy
                            ? `${inst.startedBy.firstName || ''} ${inst.startedBy.lastName || ''}`.trim()
                            : '—'}
                          {' · '}{inst.startedAt ? formatDateTimeFull(inst.startedAt) : '—'}
                          {inst.currentStepName
                            ? ` · ${localizeSeeded(inst.currentStepName, i18n.language)} (#${inst.currentStep})`
                            : ''}
                        </span>
                      </div>
                      {isTenantOwner && (
                        <button
                          type="button"
                          className="fb__active-kill"
                          onClick={() => setKillTarget(inst)}
                        >
                          <FiTrash2 /> {t('flows.builder.activeInstanceKill')}
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {!isTenantOwner && activeInstances.length > 0 && (
                <p className="fb__hint">{t('flows.builder.activeInstancesOwnerOnly')}</p>
              )}
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={!!killTarget}
        variant="danger"
        title={t('flows.builder.activeInstanceKillConfirmTitle')}
        message={t('flows.builder.activeInstanceKillConfirmMessage', {
          contract: killTarget?.contract?.name || killTarget?.contract?.number || '—',
        })}
        confirmLabel={t('flows.builder.activeInstanceKill')}
        loading={killing}
        onConfirm={killInstance}
        onCancel={() => setKillTarget(null)}
      />
    </div>
  );
}

export default FlowBuilder;
